/** เก็บ bearer token ของเจ้าหน้าที่ใน localStorage (โหมด memory ของ API ใช้ `dev-admin`) */
export const TOKEN_KEY = "sdds_admin_token";

export function getToken(): string | null {
  try {
    const v = localStorage.getItem(TOKEN_KEY);
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token.trim());
  } catch {
    // private mode / storage blocked — ผู้ใช้จะถูกส่งกลับ /login ในครั้งถัดไป
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function hasToken(): boolean {
  return getToken() !== null;
}

// โหมด memory: `dev-admin` ไม่ใช่ JWT จริง แต่ auth middleware แม็ปมันเป็น admin
// ที่ user id คงที่ตัวนี้เสมอ (ดู `DevAdminUserID` ใน middleware/auth.go)
const DEV_ADMIN_USER_ID = "22222222-2222-4222-8222-222222222201";

/**
 * ถอด claims จาก token ปัจจุบัน แบบไม่ตรวจลายเซ็น — ใช้แสดงผล/ซ่อนเมนูที่สิทธิ์
 * ไม่พอเท่านั้น ไม่ใช่กลไกความปลอดภัย (server บังคับสิทธิ์จริงผ่าน RequireRole เสมอ)
 */
function decodeClaims(): Record<string, unknown> | null {
  const token = getToken();
  if (!token || token === "dev-admin") return null;
  const [, payload] = token.split(".");
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** `sub` (user id) จาก token — ใช้เทียบกับ `assigned_officer_id` เพื่อบอกว่า "ท่านเป็นคนรับเรื่องนี้" */
export function getCurrentUserId(): string | null {
  const token = getToken();
  if (token === "dev-admin") return DEV_ADMIN_USER_ID;
  const sub = decodeClaims()?.sub;
  return typeof sub === "string" ? sub : null;
}

/** ทุก role ที่ API ออกให้ (`admin` = ชื่อเดิมของ `super_admin` ก่อน ADR-0016) */
const ROLES = [
  "citizen",
  "officer",
  "district_finance",
  "province_oversight",
  "super_admin",
  "admin",
] as const;

export type TokenRole = (typeof ROLES)[number];

/** `role` จาก token — โหมด memory ด้วย `dev-admin` เป็น super admin เสมอ */
export function getCurrentRole(): TokenRole | null {
  const token = getToken();
  if (token === "dev-admin") return "super_admin";
  const role = decodeClaims()?.role;
  return ROLES.includes(role as TokenRole) ? (role as TokenRole) : null;
}

/**
 * claim `can_sign` — ผู้ตรวจในตาแสงไม่มี จึงซ่อนฟอร์มเซ็น (server เช็คบัญชีซ้ำตอนเซ็นอยู่แล้ว
 * ค่านี้ใช้แค่ไม่โชว์ปุ่มที่กดแล้วโดนปฏิเสธ) · `dev-admin` เป็น super admin เซ็นได้เสมอ
 */
export function getCurrentCanSign(): boolean {
  if (getToken() === "dev-admin") return true;
  return decodeClaims()?.can_sign === true;
}

/** ตำแหน่ง (ADR-0030) — ต้องตรงกับ `PositionCode` ใน openapi */
export const POSITION_CODES = ["CIT", "VH", "TH", "TK", "TR", "TF", "DO", "PO", "AD", "SA"] as const;
export type TokenPosition = (typeof POSITION_CODES)[number];

/**
 * claim `pos` — ไม่มี (token ที่ออกก่อน ADR-0030) = `null` ให้ผู้เรียกถอยไปใช้ role เอง ·
 * `dev-admin` = SA ตรงกับ middleware ฝั่ง Go
 */
export function getCurrentPosition(): TokenPosition | null {
  if (getToken() === "dev-admin") return "SA";
  const pos = decodeClaims()?.pos;
  return POSITION_CODES.includes(pos as TokenPosition) ? (pos as TokenPosition) : null;
}

/**
 * claim `perms` (รหัสสิทธิ์ที่มีผล เรียงแล้ว) — **`null` = ไม่มี claim** (token เก่า/dev-admin) ต่างจาก `[]`
 * ที่แปลว่าไม่มีสิทธิ์อะไรเลย · ใช้ซ่อนเมนู/ปุ่มเท่านั้น server เช็คสิทธิ์เองทุกครั้ง
 */
export function getCurrentPermissions(): readonly string[] | null {
  const perms = decodeClaims()?.perms;
  if (!Array.isArray(perms)) return null;
  return perms.filter((p): p is string => typeof p === "string");
}

/** ขอบเขตของ token ปัจจุบัน (claim `scope` — ดู middleware.ScopeClaim ฝั่ง Go) */
export interface TokenScope {
  level: "system" | "province" | "district" | "village" | "tasaeng";
  province_id?: string;
  district_id?: string;
  village_id?: string;
  tasaeng_id?: string;
}

/**
 * ถอด scope จาก token เพื่อ **แสดงผล** เท่านั้น เช่น รู้ว่าควรโหลดรายชื่อบ้าน
 * ของเมืองไหน — ไม่ใช่กลไกความปลอดภัย server กรองด้วย scope ของจริงอีกชั้นเสมอ
 */
export function getCurrentScope(): TokenScope | null {
  if (getToken() === "dev-admin") return { level: "system" };
  const scope = decodeClaims()?.scope;
  if (scope === null || typeof scope !== "object") return null;
  const level = (scope as { level?: unknown }).level;
  if (
    level !== "system" &&
    level !== "province" &&
    level !== "district" &&
    level !== "village" &&
    level !== "tasaeng"
  ) {
    return null;
  }
  return scope as unknown as TokenScope;
}
